import { Navigate } from 'react-router-dom';

import LoadingCard from '@/components/ui/loading-card';
import { RouteConfigProvider, useRouteConfig } from '@/context/route-config';
import FuturesClientCategorisationTreatment, {
  type FuturesClientCategorisationTreatmentRouteConfig as FuturesClientCategorisationTreatmentRouteConfigBase,
} from '@/features/futures/components/client-categorisation-treatment';
import FuturesClientCategorisationLayout from '@/features/futures/layouts/client-categorisation';
import { useProfileClientCategorisation } from '@/hooks/queries/use-profile-client-categorisation';
import { useProfileIdentity } from '@/hooks/queries/use-profile-identity';

interface FuturesClientCategorisationTreatmentRouteConfig extends FuturesClientCategorisationTreatmentRouteConfigBase {
  clientCategorisation: string;
}

const FuturesClientCategorisationTreatmentLoading = () => {
  return (
    <FuturesClientCategorisationLayout progress={0.66}>
      <LoadingCard />
    </FuturesClientCategorisationLayout>
  );
};
FuturesClientCategorisationTreatmentLoading.displayName = 'FuturesClientCategorisationTreatmentLoading';

const FuturesClientCategorisationTreatmentContent = () => {
  const { isLoading: isLoadingIdentity, data: identity } = useProfileIdentity();
  const { isLoading: isLoadingClientCategorisation, data: clientCategorisation } = useProfileClientCategorisation();
  const routeConfig = useRouteConfig<FuturesClientCategorisationTreatmentRouteConfig>();
  const isLoading = isLoadingIdentity || isLoadingClientCategorisation;
  const countryOfResidency = identity?.addressOfResidency.country;

  if (isLoading) return <FuturesClientCategorisationTreatmentLoading />;

  if (!countryOfResidency) throw new Error('Unable to proceed with client categorisation, no country of residency');

  if (!clientCategorisation) return <Navigate to={routeConfig.clientCategorisation} replace />;

  return (
    <FuturesClientCategorisationLayout progress={0.66}>
      <FuturesClientCategorisationTreatment clientCategorisation={clientCategorisation} countryOfResidency={countryOfResidency} />
    </FuturesClientCategorisationLayout>
  );
};
FuturesClientCategorisationTreatmentContent.displayName = 'FuturesClientCategorisationTreatmentContent';

const FuturesClientCategorisationTreatmentPage = () => {
  return (
    <RouteConfigProvider
      config={{
        clientCategorisation: '/futures/client-categorisation',
        appropriatenessTest: '/futures/appropriateness-test',
        jurisdictionUnavailable: '/futures/client-categorisation/jurisdiction-unavailable',
        professionalAgreement: '/futures/client-categorisation/professional-agreement',
      }}
    >
      <FuturesClientCategorisationTreatmentContent />
    </RouteConfigProvider>
  );
};
FuturesClientCategorisationTreatmentPage.displayName = 'FuturesClientCategorisationTreatmentPage';

export default FuturesClientCategorisationTreatmentPage;
